import { SweepMesh } from '../geometry/sweep';

/** Fixed 1:100 — world metres → print mm via worldMetersToPrintMm. */
export const PRINT_SCALE = 100;

export interface PrintSettings {
  /** Cubic build-volume side length in millimetres. */
  chunkSizeMm: number;
  /**
   * Minimum arc distance (print mm) between a track cut and a support
   * connection. Cuts are snapped away from attaches so stub + elbow stay
   * fully inside one track chunk.
   */
  supportCutClearanceMm: number;
  /** When true, track supports are exported as separate parts. */
  separateSupport: boolean;
}

export const DEFAULT_PRINT_SETTINGS: PrintSettings = {
  chunkSizeMm: 200,
  supportCutClearanceMm: 15,
  separateSupport: false,
};

export type PrintPartKind = 'track' | 'support';

export interface PrintPart {
  id: string;
  kind: PrintPartKind;
  /** Arc-length interval for track parts; approximate for support packs. */
  s0: number;
  s1: number;
  /** Mesh in world metres (preview). STL export scales to print mm. */
  mesh: SweepMesh;
  /** Axis-aligned extents in print mm after scale (max side used for fit checks). */
  obbMm: { x: number; y: number; z: number };
  /** True when the part exceeds the build cube after every split (unsplittable). */
  oversized: boolean;
  /** True when Manifold STL fixer produced a solid (false = cleaned triangle soup fallback). */
  fixed: boolean;
  /** Optional fixer note (skipped pieces, cavity skip, etc.). */
  fixWarning?: string;
}

/**
 * One split fragment before packing into a print chunk (track interval, hub,
 * half-beam, footer, …). Preview-only — not a downloadable solid.
 */
export interface PrintAtom {
  id: string;
  /** Print chunk this atom was packed into (`track-001` / `support-001`). */
  chunkId: string;
  kind: PrintPartKind;
  mesh: SweepMesh;
}

export interface PrintJob {
  settings: PrintSettings;
  /** Interior cut positions along the track (excludes 0 and totalLength). */
  cuts: number[];
  parts: PrintPart[];
  /** Split fragments shown in the "parts" print preview. */
  atoms: PrintAtom[];
  warnings: string[];
  /** Diagnostic lines from the generate pipeline (copy/paste friendly). */
  log: string[];
}

/** Convert a world-space length/coordinate (metres) to print millimetres at PRINT_SCALE. */
export function worldMetersToPrintMm(metres: number): number {
  return (metres / PRINT_SCALE) * 1000;
}

/** Convert print millimetres back to world metres at PRINT_SCALE. */
export function printMmToWorldMetres(mm: number): number {
  return (mm * PRINT_SCALE) / 1000;
}

export function normalizePrintSettings(
  partial: Partial<PrintSettings> | null | undefined,
): PrintSettings {
  return {
    chunkSizeMm:
      partial?.chunkSizeMm != null && Number.isFinite(partial.chunkSizeMm)
        ? Math.min(350, Math.max(80, partial.chunkSizeMm))
        : DEFAULT_PRINT_SETTINGS.chunkSizeMm,
    supportCutClearanceMm:
      partial?.supportCutClearanceMm != null && Number.isFinite(partial.supportCutClearanceMm)
        ? Math.min(50, Math.max(0, partial.supportCutClearanceMm))
        : DEFAULT_PRINT_SETTINGS.supportCutClearanceMm,
    separateSupport: partial?.separateSupport === true,
  };
}
